import React, { useContext } from 'react';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Text from '../../../../../Components/Base/Text/Text';
import FormWrapper from '../../../../../Components/Form/FormWrapper';
import FormCondition from '../../../../../Components/Form/FormCondition';
import FormContainer from './../../../../../Components/Form/FormContainer';
import {
	FieldNames,
	HealthPlanContext,
} from '../../../../../Services/HealthBabyPlanDataContext';
import SubmitButton from '../../../../../Components/Button/SubmitButton';
import useTranslation from '../../../../../Hooks/useTranslation';
import NumberField from '../../../../../Components/Form/components/NumberField';
import InputField from '../../../../../Components/Form/components/InputField';
import SelectField from '../../../../../Components/Form/components/SelectField';
import RadioListField from '../../../../../Components/Form/components/RadioListField';
import TextAreaField from '../../../../../Components/Form/components/TextAreaField';
import FormMultiplier from '../../../../../Components/Form/FormMultiplier';
import ScrollContainer from '../../../../Base/Components/ScrollContainer';
//@ts-ignore
import styles from './PreviousPregnancy.module.scss';
import {
	BIRTH_METHOD_OPTIONS,
	GENDER_RADIO_OPTIONS,
} from '../../../../HealthData/constants/common';
import SavingSpinner from '../../../../../Components/Base/Loading/SavingSpinner';
import useFormDraft from '../../../../../Components/Form/FormDraftState/useFormDraft';
import { FormDraftStateKey } from '../../../../../Components/Form/FormDraftState/FormDraftStateKey';

enum PREGNANCY_METHOD_TYPES {
	BIRTH = 'birth',
	ABORTION = 'abortion',
	OTHER = 'other',
}

enum ABORTION_METHOD_TYPES {
	UPT = 'upt',
	SPONTANEOUS = 'spontaneous',
	INDUCED = 'induced',
}

enum PREGNANCY_METHODS {
	SPONTANEOUS_CONCEPTION = 'spontaneousConception',
	IVF = 'ivf',
}

export enum PREGNANCY_COURSE {
	COMPLICATED = 'complicated',
	WITHOUT_COMPLICATIONS = 'withoutComplications',
}

const VALIDATION_TRANSLATION_PREFIX = 'validation.previousPregnancyForm';

const getValidationSchema = (t: (key: string) => string | undefined) =>
	yup.object({
		[FieldNames.PREVIOUS_PREGNANCIES]: yup.array().of(
			yup.object().shape({
				details: yup
					.object({
						type: yup
							.string()
							.nullable()
							.required(t(`${VALIDATION_TRANSLATION_PREFIX}.type`)),
						birthAdditionalInformation: yup.string().nullable(),
						birthWeek: yup.string().when('type', {
							is: PREGNANCY_METHOD_TYPES.BIRTH,
							then: yup
								.string()
								.nullable()
								.required(t(`${VALIDATION_TRANSLATION_PREFIX}.birthWeek`)),
						}),
						birthOfChild: yup
							.string()
							.when('type', {
								is: PREGNANCY_METHOD_TYPES.BIRTH,
								then: yup
									.string()
									.nullable()
									.required(t(`${VALIDATION_TRANSLATION_PREFIX}.birthOfChild`)),
							})
							.nullable(),
						birthMethod: yup
							.object({
								label: yup.string(),
								value: yup.string(),
							})
							.when('type', {
								is: PREGNANCY_METHOD_TYPES.BIRTH,
								then: yup
									.object({
										label: yup.string().nullable().required(),
										value: yup.string().nullable().required(),
									})
									.nullable()
									.required(t(`${VALIDATION_TRANSLATION_PREFIX}.birthMethod`)),
							})
							.nullable(),
						courseOfChildBirthBaby: yup
							.string()
							.when('type', {
								is: PREGNANCY_METHOD_TYPES.BIRTH,
								then: yup
									.string()
									.nullable()
									.required(
										t(
											`${VALIDATION_TRANSLATION_PREFIX}.courseOfChildBirthBaby`,
										),
									),
							})
							.nullable(),
						courseOfChildBirthBabyComplications: yup
							.string()
							.when(['type', 'courseOfChildBirthBaby'], {
								is: (
									type: PREGNANCY_METHOD_TYPES,
									courseOfChildBirthBaby: PREGNANCY_COURSE,
								) =>
									type === PREGNANCY_METHOD_TYPES.BIRTH &&
									courseOfChildBirthBaby === PREGNANCY_COURSE.COMPLICATED,
								then: yup
									.string()
									.nullable()
									.required(
										`${VALIDATION_TRANSLATION_PREFIX}.complicationsDescription`,
									),
							}),
						courseOfChildBirthMother: yup
							.string()
							.when('type', {
								is: PREGNANCY_METHOD_TYPES.BIRTH,
								then: yup
									.string()
									.nullable()
									.required(
										t(
											`${VALIDATION_TRANSLATION_PREFIX}.courseOfChildBirthMother`,
										),
									),
							})
							.nullable(),
						courseOfChildBirthMotherComplications: yup
							.string()
							.when(['type', 'courseOfChildBirthMother'], {
								is: (
									type: PREGNANCY_METHOD_TYPES,
									courseOfChildBirthMother: PREGNANCY_COURSE,
								) =>
									type === PREGNANCY_METHOD_TYPES.BIRTH &&
									courseOfChildBirthMother === PREGNANCY_COURSE.COMPLICATED,
								then: yup
									.string()
									.nullable()
									.required(
										t(
											`${VALIDATION_TRANSLATION_PREFIX}.complicationsDescription`,
										),
									),
							}),
						methodOfAbortion: yup.string().when(['type'], {
							is: PREGNANCY_METHOD_TYPES.ABORTION,
							then: yup
								.string()
								.nullable()
								.required(
									t(`${VALIDATION_TRANSLATION_PREFIX}.methodOfAbortion`),
								),
						}),
						courseOfAbortion: yup.string().when(['type'], {
							is: PREGNANCY_METHOD_TYPES.ABORTION,
							then: yup
								.string()
								.nullable()
								.required(
									t(`${VALIDATION_TRANSLATION_PREFIX}.courseOfAbortion`),
								),
						}),
						otherMethod: yup.string().when(['type'], {
							is: PREGNANCY_METHOD_TYPES.OTHER,
							then: yup
								.string()
								.nullable()
								.required(t(`${VALIDATION_TRANSLATION_PREFIX}.otherMethod`)),
						}),
						courseOfAbortionComplications: yup
							.string()
							.when(['type', 'courseOfAbortion'], {
								is: (
									type: PREGNANCY_METHOD_TYPES,
									courseOfAbortion: PREGNANCY_COURSE,
								) =>
									type === PREGNANCY_METHOD_TYPES.ABORTION &&
									courseOfAbortion === PREGNANCY_COURSE.COMPLICATED,
								then: yup
									.string()
									.nullable()
									.required(
										t(
											`${VALIDATION_TRANSLATION_PREFIX}.complicationsDescription`,
										),
									),
							}),
						newbornWeight: yup.string().when('type', {
							is: PREGNANCY_METHOD_TYPES.BIRTH,
							then: yup
								.string()
								.nullable()
								.required(t(`${VALIDATION_TRANSLATION_PREFIX}.newbornWeight`)),
						}),
						abortionWeek: yup.string().when('methodOfAbortion', {
							is: (methodOfAbortion: ABORTION_METHOD_TYPES) =>
								[
									ABORTION_METHOD_TYPES.INDUCED,
									ABORTION_METHOD_TYPES.SPONTANEOUS,
									ABORTION_METHOD_TYPES.UPT,
								].includes(methodOfAbortion),
							then: yup
								.string()
								.nullable()
								.required(t(`${VALIDATION_TRANSLATION_PREFIX}.abortionWeek`)),
						}),
						abortionReason: yup
							.object({
								label: yup.string(),
								value: yup.string(),
							})
							.when('methodOfAbortion', {
								is: ABORTION_METHOD_TYPES.SPONTANEOUS,
								then: yup
									.object({
										label: yup.string(),
										value: yup.string(),
									})
									.nullable()
									.required(
										t(`${VALIDATION_TRANSLATION_PREFIX}.abortionReason`),
									),
							})
							.nullable(),
						pregnancyMethodDescription: yup.string().when('type', {
							is: PREGNANCY_METHOD_TYPES.OTHER,
							then: yup
								.string()
								.required(
									t(
										`${VALIDATION_TRANSLATION_PREFIX}.pregnancyMethodDescription`,
									),
								)
								.nullable(),
						}),
					})
					.required(),

				pregnancyMethod: yup
					.mixed<PREGNANCY_METHODS>()
					.oneOf(Object.values(PREGNANCY_METHODS))
					.nullable()
					.required(t(`${VALIDATION_TRANSLATION_PREFIX}.pregnancyMethod`)),
			}),
		),
	});

interface Props {
	onSuccess: () => void;
	patientId: string;
}

const PreviousPregnancyForm: React.FC<Props> = ({ onSuccess, patientId }) => {
	const { t } = useTranslation();
	const { data, setData } = useContext(HealthPlanContext);

	const totalNumberOfPregnancies = Number(
		data[FieldNames.CURRENT_PREGNANCY] //@ts-ignore
			?.totalNumberOfPregnanciesIncludingCurrent ?? 0,
	);

	const defaultValues = {
		[FieldNames.PREVIOUS_PREGNANCIES]: Boolean(
			data[FieldNames.PREVIOUS_PREGNANCIES],
		)
			? data[FieldNames.PREVIOUS_PREGNANCIES]
			: totalNumberOfPregnancies > 1
			? Array(totalNumberOfPregnancies - 1).fill({})
			: undefined,
	};

	const formMethods = useForm({
		defaultValues: defaultValues,
		resolver: yupResolver(getValidationSchema(t)),
	});

	return (
		<ScrollContainer>
			<div className='col-12 col-lg-8'>
				<div className='page-panel mt-1'>
					{totalNumberOfPregnancies <= 1 && (
						<Text
							tx='base.noPreviousPregnancies'
							className={styles.emptyFormMessage}
						/>
					)}
					<FormWrapper formMethods={formMethods}>
						<FormMultiplier
							name={FieldNames.PREVIOUS_PREGNANCIES}
							allowAddForm={false}>
							{({ index, update, field }) => (
								<div className={styles.form}>
									<h3 className={styles.title}>
										<Text>
											<Text inline>{index + 1}.</Text>{' '}
											<Text inline tx='base.pregnancy' />
										</Text>
									</h3>
									<RadioListField
										isRequired
										name='details.type'
										className={styles.field}
										data={[
											{
												title: 'base.birth',
												value: PREGNANCY_METHOD_TYPES.BIRTH,
											},
											{
												title: 'base.abortion',
												value: PREGNANCY_METHOD_TYPES.ABORTION,
											},
											{
												title: 'base.other',
												value: PREGNANCY_METHOD_TYPES.OTHER,
											},
										]}
										onFieldChange={() => {
											// Reset selected fields after user switch between pregnancy type.
											update(index, {});
										}}
										inLine
									/>
									<RadioListField
										isRequired
										label='base.pregnancyMethod'
										name='pregnancyMethod'
										className={styles.field}
										data={[
											{
												title: 'base.spontaneousConception',
												value: PREGNANCY_METHODS.SPONTANEOUS_CONCEPTION,
											},
											{
												title: 'base.ivf',
												value: PREGNANCY_METHODS.IVF,
											},
										]}
										inLine
									/>
									<FormCondition
										controlName='details.type'
										equal={PREGNANCY_METHOD_TYPES.BIRTH}>
										<SelectField
											isRequired
											label='base.birthMethod'
											name='details.birthMethod'
											className={styles.field}
											options={BIRTH_METHOD_OPTIONS}
										/>
										<RadioListField
											isRequired
											label='base.birthOfChild'
											name='details.birthOfChild'
											className={styles.field}
											data={GENDER_RADIO_OPTIONS}
											inLine
										/>
										<NumberField
											isRequired
											label='base.newbornWeight'
											name='details.newbornWeight'
											className={styles.field}
											suffix='g'
										/>
										<NumberField
											isRequired
											name='details.birthWeek'
											label='base.birthWeek'
											className={styles.field}
										/>
										<RadioListField
											isRequired
											label='base.courseOfChildBirthBaby'
											name='details.courseOfChildBirthBaby'
											className={styles.field}
											data={[
												{
													title: 'base.withoutComplications',
													value: PREGNANCY_COURSE.WITHOUT_COMPLICATIONS,
												},
												{
													title: 'base.complicated',
													value: PREGNANCY_COURSE.COMPLICATED,
												},
											]}
											inLine>
											<FormCondition
												controlName='details.courseOfChildBirthBaby'
												equal={PREGNANCY_COURSE.COMPLICATED}>
												<FormContainer settings={{ showLine: true }}>
													<InputField
														isRequired
														label='base.complicationsDescription'
														name='details.courseOfChildBirthBabyComplications'
														className={styles.field}
													/>
												</FormContainer>
											</FormCondition>
										</RadioListField>
										<RadioListField
											isRequired
											label='base.courseOfChildBirthMother'
											name='details.courseOfChildBirthMother'
											className={styles.field}
											data={[
												{
													title: 'base.withoutComplications',
													value: PREGNANCY_COURSE.WITHOUT_COMPLICATIONS,
												},
												{
													title: 'base.complicated',
													value: PREGNANCY_COURSE.COMPLICATED,
												},
											]}
											inLine>
											<FormCondition
												controlName='details.courseOfChildBirthMother'
												equal={PREGNANCY_COURSE.COMPLICATED}>
												<FormContainer settings={{ showLine: true }}>
													<InputField
														isRequired
														label='base.complicationsDescription'
														name='details.courseOfChildBirthMotherComplications'
														className={styles.field}
													/>
												</FormContainer>
											</FormCondition>
										</RadioListField>
										<TextAreaField
											label='base.additionalInformation'
											name='details.birthAdditionalInformation'
											className={styles.field}
										/>
									</FormCondition>
									<FormCondition
										controlName='details.type'
										equal={PREGNANCY_METHOD_TYPES.ABORTION}>
										<RadioListField
											isRequired
											label='base.methodOfAbortion'
											name='details.methodOfAbortion'
											className={styles.field}
											data={[
												{ title: 'base.upt', value: ABORTION_METHOD_TYPES.UPT },
												{
													title: 'base.spontaneous',
													value: ABORTION_METHOD_TYPES.SPONTANEOUS,
												},
												{
													title: 'base.induced',
													value: ABORTION_METHOD_TYPES.INDUCED,
												},
											]}
											inLine>
											<FormCondition
												controlName='details.methodOfAbortion'
												equal={ABORTION_METHOD_TYPES.UPT}>
												<NumberField
													isRequired
													label='base.abortionWeek'
													name='details.abortionWeek'
													className={styles.field}
												/>
												<TextAreaField
													label='base.additionalInformation'
													name='details.weekOfAbortionDetail'
													className={styles.field}
												/>
											</FormCondition>
											<FormCondition
												controlName='details.methodOfAbortion'
												equal={ABORTION_METHOD_TYPES.SPONTANEOUS}>
												<NumberField
													isRequired
													label='base.abortionWeek'
													name='details.abortionWeek'
													className={styles.field}
												/>
												<SelectField
													isRequired
													label='base.abortionReason'
													name='details.abortionReason'
													className={styles.field}
													options={[
														{
															value: 'hiddenPregnancy',
															label: 'base.hiddenPregnancy',
														},
														{
															value: 'bleeding',
															label: 'base.bleeding',
														},
														{
															value: 'contraction',
															label: 'base.contraction',
														},
														{
															value: 'drainageOfAmnioticFluid',
															label: 'base.drainageOfAmnioticFluid',
														},
													]}
												/>
												<TextAreaField
													label='base.additionalInformation'
													name='details.weekOfAbortionDetail'
													className={styles.field}
												/>
											</FormCondition>
											<FormCondition
												controlName='details.methodOfAbortion'
												equal={ABORTION_METHOD_TYPES.INDUCED}>
												<NumberField
													isRequired
													label='base.abortionWeek'
													name='details.abortionWeek'
													className={styles.field}
												/>
												<TextAreaField
													label='base.additionalInformation'
													name='details.weekOfAbortionDetail'
													className={styles.field}
												/>
											</FormCondition>
										</RadioListField>
										<RadioListField
											isRequired
											label='base.courseOfAbortion'
											name='details.courseOfAbortion'
											className={styles.field}
											data={[
												{
													title: 'base.withoutComplications',
													value: PREGNANCY_COURSE.WITHOUT_COMPLICATIONS,
												},
												{
													title: 'base.complications',
													value: PREGNANCY_COURSE.COMPLICATED,
												},
											]}
											inLine>
											<FormCondition
												controlName='details.courseOfAbortion'
												equal={PREGNANCY_COURSE.COMPLICATED}>
												<FormContainer settings={{ showLine: true }}>
													<InputField
														isRequired
														name='details.courseOfAbortionComplications'
														label='base.complicationsDescription'
														className={styles.field}
													/>
												</FormContainer>
											</FormCondition>
										</RadioListField>
									</FormCondition>
									<FormCondition
										controlName='details.type'
										equal={PREGNANCY_METHOD_TYPES.OTHER}>
										<RadioListField
											isRequired
											name='details.otherMethod'
											label='base.choosePregnancy'
											className={styles.field}
											data={[
												{
													title: 'base.ectopicPregnancy',
													value: 'ectopicPregnancy',
												},
												{
													title: 'base.molarPregnancy',
													value: 'molarPregnancy',
												},
												{
													title: 'base.otherPregnancy',
													value: 'otherPregnancy',
												},
											]}
											inLine
										/>
										<FormCondition
											controlName='details.otherMethod'
											in={[
												'ectopicPregnancy',
												'molarPregnancy',
												'otherPregnancy',
											]}>
											<InputField
												isRequired
												name='details.pregnancyMethodDescription'
												label='base.additionalInformation'
												className={styles.field}
											/>
										</FormCondition>
									</FormCondition>
								</div>
							)}
						</FormMultiplier>
						<div className='d-flex mt-4 justify-content-between'>
							<SubmitButton
								title={'base.shared.continue'}
								onSubmit={fields => {
									setData(fields);
									onSuccess();
								}}
							/>
						</div>
					</FormWrapper>
				</div>
			</div>
		</ScrollContainer>
	);
};

export default PreviousPregnancyForm;
